import jacket from "@/assets/product-jacket.jpg";
import { supabase } from "@/integrations/supabase/client";

export type ProductVariant = {
  id: string;
  product_id: string;
  color_name: string;
  color_hex: string;
  image_url: string;
  is_default: boolean;
  sort_order: number;
};

export type Product = {
  id?: string;
  slug: string;
  name: string;
  category: string;
  subcategory?: string;
  leather_type?: string;
  price: number;
  image: string;
  image_url?: string;
  hover_image_url?: string;
  gallery_images?: string[];
  tagline: string;
  description: string;
  story?: string;
  details: string[];
  tags: string[];
  is_active?: boolean;
  sort_order?: number;
  stock_quantity?: number;
  low_stock_threshold?: number;
  variants?: ProductVariant[];
};

export function resolveImage(image_url: string | null | undefined): string {
  if (!image_url) return jacket;
  if (image_url.startsWith("asset:")) return jacket;
  return image_url;
}

function normalizeGalleryImages(images: unknown): string[] {
  if (!Array.isArray(images)) return [];
  return images.filter((value): value is string => typeof value === "string" && value.length > 0);
}

export function getProductGallery(product: Product): string[] {
  return Array.from(
    new Set(
      [product.image, ...(product.gallery_images ?? []), product.hover_image_url].filter(
        (value): value is string => typeof value === "string" && value.length > 0,
      ),
    ),
  );
}

type ProductRow = {
  id: string;
  slug: string;
  name: string;
  category: string;
  subcategory: string | null;
  leather_type: string | null;
  price: number;
  description: string;
  story: string | null;
  image_url: string;
  hover_image_url: string | null;
  gallery_images: unknown;
  details: unknown;
  tags: string[] | null;
  is_active: boolean;
  sort_order: number;
  stock_quantity?: number | null;
  low_stock_threshold?: number | null;
};

function rowToProduct(row: ProductRow): Product {
  const details = Array.isArray(row.details) ? (row.details as string[]) : [];
  const tagline = row.description.split(". ")[0]?.replace(/\.$/, "") + ".";
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    category: row.category,
    subcategory: row.subcategory ?? "",
    leather_type: row.leather_type ?? "",
    price: Number(row.price),
    image: resolveImage(row.image_url),
    image_url: row.image_url,
    hover_image_url: row.hover_image_url ?? "",
    gallery_images: normalizeGalleryImages(row.gallery_images),
    tagline,
    description: row.description,
    story: row.story ?? "",
    details,
    tags: row.tags ?? [],
    is_active: row.is_active,
    sort_order: row.sort_order,
    stock_quantity: row.stock_quantity ?? 0,
    low_stock_threshold: row.low_stock_threshold ?? 5,
  };
}

// Public, customer-facing columns. Excludes internal inventory fields
// (stock_quantity, low_stock_threshold) — those columns are also revoked at
// the GRANT level so they cannot be read by anon/authenticated, even via *.
const PUBLIC_PRODUCT_COLUMNS =
  "id, slug, name, category, subcategory, leather_type, price, description, story, image_url, hover_image_url, gallery_images, details, tags, is_active, sort_order";

export async function fetchProducts(_opts?: { includeInactive?: boolean }): Promise<Product[]> {
  // Public path — column allow-list, no inventory fields.
  const { data, error } = await supabase
    .from("products")
    .select(`${PUBLIC_PRODUCT_COLUMNS}, product_variants(*)`)
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<ProductRow & { product_variants: ProductVariant[] | null }>;
  return rows.map((row) => {
    const product = rowToProduct(row);
    const variants = (row.product_variants ?? []).slice().sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );
    product.variants = variants;
    return product;
  });
}

export async function fetchProductBySlug(slug: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from("products")
    .select(PUBLIC_PRODUCT_COLUMNS)
    .eq("slug", slug)
    .eq("is_active", true)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const product = rowToProduct(data as unknown as ProductRow);

  const { data: variantData, error: variantError } = await supabase
    .from("product_variants")
    .select("*")
    .eq("product_id", product.id!)
    .order("sort_order", { ascending: true });
  if (variantError) throw variantError;
  product.variants = (variantData ?? []) as ProductVariant[];
  return product;
}

export const CATEGORIES = ["Jackets", "Bags", "Wallets", "Accessories", "Riders"] as const;
export const TAGS = ["men", "women", "unisex", "new-arrivals", "accessories", "riders"] as const;
export const ACCESSORY_SUBS = ["belts", "gloves", "watch-straps", "keychains", "cardholders", "passport-covers"] as const;
